import { createElement } from './helpers.js';
import { createLogger } from '../logging/logger.js';

const log = createLogger({ module: 'reorder-controls' });

/**
 * ONE reusable reorder-controls cluster — an optional drag handle + optional ▲/▼ Move up/down
 * buttons — shared by every card/row that supports reordering (photo gallery, videos, form-builder
 * fields/fieldsets/options). Collapses the previously-duplicated per-card markup + boundary/focus/
 * disabled state into a single owner.
 *
 * Reorder is operable two ways where both are present: drag the handle (mouse) OR the Move buttons
 * (keyboard/touch — HTML5 drag-and-drop does not fire on touch, so the buttons are the accessible
 * path). `withHandle` and `withMoveButtons` are INDEPENDENT: a card can render handle-only (drag,
 * no buttons — e.g. an image-card whose move callbacks are omitted) or buttons-only (option rows,
 * which have no handle).
 *
 * Accessibility: every control is labelled from `ariaNoun` ("Move photo up", "Drag to reorder
 * field"); the ▲/▼ glyphs are decorative text. Boundary buttons disable via `setMoveState`.
 * `focusMove` re-homes focus onto a Move button after the host re-renders (the host destroys +
 * rebuilds the controls on reorder, which would otherwise drop focus to <body>).
 *
 * Lifecycle: listeners are registered with an `AbortController` signal so `destroy()` fully
 * unregisters them (removing the DOM node alone does NOT detach listeners while a handle is
 * retained). Hosts that rebuild their rows MUST `destroy()` the prior controls before re-creating.
 *
 * @param {Object} args
 * @param {string} [args.key] - Stable key echoed back in the move callbacks.
 * @param {string} args.ariaNoun - Noun for aria-labels, e.g. 'photo' | 'video' | 'field' | 'fieldset' | 'option'.
 * @param {'row' | 'column'} [args.orientation] - Controls flex direction. Default 'row'.
 * @param {boolean} [args.withHandle] - Render the drag handle. Default true.
 * @param {boolean} [args.withMoveButtons] - Render the ▲/▼ Move buttons. Default true. Requires BOTH callbacks.
 * @param {(key: string) => void} [args.onMoveUp] - Required when `withMoveButtons`.
 * @param {(key: string) => void} [args.onMoveDown] - Required when `withMoveButtons`.
 * @returns {{ el: HTMLElement, handle: HTMLElement | null, setMoveState: (canUp: boolean, canDown: boolean) => void, focusMove: (preferUp: boolean) => void, setDisabled: (d: boolean) => void, destroy: () => void }}
 */
export function createReorderControls({
  key = '',
  ariaNoun,
  orientation = 'row',
  withHandle = true,
  withMoveButtons = true,
  onMoveUp,
  onMoveDown,
} = /** @type {*} */ ({})) {
  let disabled = false;
  let canMoveUp = true;
  let canMoveDown = true;

  const controller = new AbortController();
  const { signal } = controller;

  const el = createElement('div', {
    class: `reorder-controls reorder-controls--${orientation === 'column' ? 'column' : 'row'}`,
  });

  // Move buttons require BOTH callbacks — a missing one is a programmer error (all consumers pass
  // both when they ask for buttons). Fail loud in dev, degrade safe: don't render a dead button.
  const renderMoveButtons = withMoveButtons && !!onMoveUp && !!onMoveDown;
  if (withMoveButtons && !renderMoveButtons) {
    log.error('withMoveButtons requires both onMoveUp and onMoveDown', { data: { ariaNoun } });
  }
  if (!withHandle && !renderMoveButtons) {
    // "none" combo — unsupported, but return the complete stable interface (no partial shape) so
    // consumers/tests can rely on the return contract regardless.
    log.error('reorder-controls created with neither a handle nor move buttons', { data: { ariaNoun } });
  }

  /** @type {HTMLElement | null} */
  let handle = null;
  if (withHandle) {
    handle = createElement('div', {
      class: 'reorder-controls__handle', draggable: 'true',
      'aria-label': `Drag to reorder ${ariaNoun}`, title: 'Drag to reorder',
    }, ['⠿']);
    el.appendChild(handle);
  }

  /** @type {HTMLButtonElement | null} */
  let moveUpBtn = null;
  /** @type {HTMLButtonElement | null} */
  let moveDownBtn = null;
  if (renderMoveButtons) {
    moveUpBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button', class: 'reorder-controls__move reorder-controls__move--up',
      'aria-label': `Move ${ariaNoun} up`, title: 'Move up',
    }, ['▲']));
    moveDownBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button', class: 'reorder-controls__move reorder-controls__move--down',
      'aria-label': `Move ${ariaNoun} down`, title: 'Move down',
    }, ['▼']));
    moveUpBtn.addEventListener('click', () => { if (!disabled && canMoveUp) onMoveUp?.(key); }, { signal });
    moveDownBtn.addEventListener('click', () => { if (!disabled && canMoveDown) onMoveDown?.(key); }, { signal });
    el.appendChild(moveUpBtn);
    el.appendChild(moveDownBtn);
  }

  function applyMoveDisabled() {
    if (moveUpBtn) moveUpBtn.disabled = disabled || !canMoveUp;
    if (moveDownBtn) moveDownBtn.disabled = disabled || !canMoveDown;
  }

  return {
    el,
    handle,
    /** @param {boolean} up @param {boolean} down — boundary state (first can't move up; last can't move down). */
    setMoveState(up, down) {
      canMoveUp = up;
      canMoveDown = down;
      applyMoveDisabled();
    },
    /**
     * Re-home focus onto a Move button after a reorder rebuilt the controls. Prefer the
     * same-direction button; fall back to the opposite one when the moved item landed on a
     * boundary (the preferred button is now disabled). No-op when buttons weren't rendered.
     * @param {boolean} preferUp
     */
    focusMove(preferUp) {
      const primary = preferUp ? moveUpBtn : moveDownBtn;
      const fallback = preferUp ? moveDownBtn : moveUpBtn;
      if (primary && !primary.disabled) primary.focus();
      else if (fallback && !fallback.disabled) fallback.focus();
    },
    /** @param {boolean} d */
    setDisabled(d) {
      disabled = d;
      if (handle) handle.setAttribute('draggable', String(!d));
      applyMoveDisabled();
    },
    destroy() {
      controller.abort(); // detach all listeners (removing the node alone does not)
      el.remove();
    },
  };
}
